import React, {useEffect, useState} from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Grid,
  Typography,
  makeStyles,
  Snackbar, Portal
} from '@material-ui/core';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import { t } from '../common/LocalizationProvider';
import {devicesActions, devicesSelectedActions, periodActions} from '../store'
import Alert from "@material-ui/lab/Alert";
import CircularProgress from "@material-ui/core/CircularProgress";
import {useLocation} from "react-router-dom";
import {cyberStyles} from "../cssShared/cyberTheme";

const useStyles = makeStyles(theme => ({
  ...cyberStyles(theme),
}));

const ReportFilter = React.memo(({ children, handleSubmit, showOnly, openSnackbar,
                                   setOpenSnackbar, handleStopsPositions, loading, setLoading}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const path = useLocation();

  const devices = useSelector(state => Object.values(state.devices.items));
  const deviceIds = useSelector(state => state.devicesSelected.devicesSelected ?? []);
  const period = useSelector(state => state.period.period);

  const [from, setFrom] = useState(moment().subtract(1, 'hour'));
  const [to, setTo] = useState(moment());

  const handleClick = (mail, json) => {
    let selectedFrom;
    let selectedTo;
    switch (period) {
      case 'today':
        selectedFrom = moment().startOf('day');
        selectedTo = moment().endOf('day');
        break;
      case 'yesterday':
        selectedFrom = moment().subtract(1, 'day').startOf('day');
        selectedTo = moment().subtract(1, 'day').endOf('day');
        break;
      case 'thisWeek':
        selectedFrom = moment().startOf('week');
        selectedTo = moment().endOf('week');
        break;
      case 'previousWeek':
        selectedFrom = moment().subtract(1, 'week').startOf('week');
        selectedTo = moment().subtract(1, 'week').endOf('week');
        break;
      case 'thisMonth':
        selectedFrom = moment().startOf('month');
        selectedTo = moment().endOf('month');
        break;
      case 'previousMonth':
        selectedFrom = moment().subtract(1, 'month').startOf('month');
        selectedTo = moment().subtract(1, 'month').endOf('month');
        break;
      default:
        selectedFrom = from;
        selectedTo = to;
        break;
    }

    if (period === 'custom') {
      const diffDays = to.diff(from, 'days');
      if (diffDays > 31 || diffDays < 0) {
        setOpenSnackbar('exceedAllowedDays');
        return;
      }
    }
    const accept = json ? 'application/json' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if ((path?.pathname?.includes("chart") || path?.pathname?.includes("replay")) && deviceIds?.length > 1) {
      setOpenSnackbar('selectOneDevice')
      return;
    }
    if (!mail && json)
      setLoading('hideShow')
    if (!mail && !json)
      setLoading('hideExport')
    if (mail && !json)
      setLoading('hideEmailExport')

    handleSubmit(
        deviceIds.filter(id => id !== 0),
        selectedFrom.toISOString(),
        selectedTo.toISOString(),
        mail,
        { Accept: accept }
    );
    if (handleStopsPositions)
      handleStopsPositions(
          deviceIds.filter(id => id !== 0),
          selectedFrom.toISOString(),
          selectedTo.toISOString(),
          mail,
          { Accept: accept }
      );
  }

  useEffect(() => {
    if (deviceIds.some(id => id !== 0)) {
      if ((path.pathname.includes("chart") || path.pathname.includes("replay")) && deviceIds.length > 1)
        return;
      else
        handleClick(false, true);
    }
    }, []);

  useEffect(() => {
    let getDevices = async () => {
      if (devices.length === 0) {
        const response = await fetch('/api/devices?all=true');
        if (response.ok)
          dispatch(devicesActions.refresh(await response.json()));
        else
          setOpenSnackbar('UnknownProblem');
      }
    }
    getDevices()
  }, []);

  const handleSelectChange = (event) => {
    const selectedDeviceId = event.target.value;
    dispatch(devicesSelectedActions.updateDevicesSelected(Array.isArray(selectedDeviceId) ? selectedDeviceId : [selectedDeviceId]));
  };

  return (
      <Grid container spacing={2} className={classes.gridContainer}>
        <Grid item xs={12} sm={period === 'custom' ? 3 : 6}>
          <FormControl variant="filled" fullWidth className={classes.formControl}>
            <InputLabel>{t('sharedDevice')}</InputLabel>
            <Select
                multiple={!path.pathname.includes("chart") && !path.pathname.includes("replay")}
                value={Array.isArray(deviceIds) ? deviceIds : (deviceIds ? [deviceIds] : [])}
                onChange={handleSelectChange}
                MenuProps={{PaperProps: { className: classes.periodMenuPaper },}}
            >
              {devices.map((device) => (
                  <MenuItem key={device.id} value={device.id} className={classes.menuItem}>{device.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={period === 'custom' ? 3 : 6}>
          <FormControl variant="filled" fullWidth className={classes.formControl}>
            <InputLabel>{t('reportPeriod')}</InputLabel>
            <Select
                value={period}
                onChange={(e) => dispatch(periodActions.update(e.target.value))}
                MenuProps={{PaperProps: { className: classes.periodMenuPaper },}}
            >
              <MenuItem value="today" className={classes.menuItem}>{t('reportToday')}</MenuItem>
              <MenuItem value="yesterday" className={classes.menuItem}>{t('reportYesterday')}</MenuItem>
              <MenuItem value="thisWeek" className={classes.menuItem}>{t('reportThisWeek')}</MenuItem>
              <MenuItem value="previousWeek" className={classes.menuItem}>{t('reportPreviousWeek')}</MenuItem>
              <MenuItem value="thisMonth" className={classes.menuItem}>{t('reportThisMonth')}</MenuItem>
              <MenuItem value="previousMonth" className={classes.menuItem}>{t('reportPreviousMonth')}</MenuItem>
              <MenuItem value="custom" className={classes.menuItem}>{t('reportCustom')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {period === 'custom' && <Grid item xs={12} sm={3}>
          <TextField
              className={classes.formControl}
              variant="filled"
              label={t('reportFrom')}
              type="datetime-local"
              value={from.format(moment.HTML5_FMT.DATETIME_LOCAL)}
              onChange={e => setFrom(moment(e.target.value, moment.HTML5_FMT.DATETIME_LOCAL))}
              fullWidth />
        </Grid>}
        {period === 'custom' && <Grid item xs={12} sm={3}>
          <TextField
              className={classes.formControl}
              variant="filled"
              label={t('reportTo')}
              type="datetime-local"
              value={to.format(moment.HTML5_FMT.DATETIME_LOCAL)}
              inputProps={{
                max: from.clone().add(31, 'days').format(moment.HTML5_FMT.DATETIME_LOCAL),
                min: from.format(moment.HTML5_FMT.DATETIME_LOCAL)
              }}
              onChange={e => setTo(moment(e.target.value, moment.HTML5_FMT.DATETIME_LOCAL))}
              fullWidth />
        </Grid>}
        {children}
        <Grid item xs={!showOnly ? 4 : 12} sm={!showOnly ? 2 : 6}>
          <Button
              className={classes.cyberButton}
              onClick={() => handleClick(false, true)}
              variant="outlined"
              fullWidth
              disabled={loading === 'hideShow'}
          >
            {loading === 'hideShow' ? (<CircularProgress color="secondary" size={18} style={{ marginLeft: '8px' }} />) : t('reportShow')}
          </Button>
        </Grid>

        {!showOnly &&
            <Grid item xs={4} sm={2}>
              <Button
                  className={classes.cyberButton}
                  onClick={() => handleClick(false, false)}
                  variant='outlined'
                  fullWidth
                  disabled={loading === 'hideExport'}
              >
                {loading === 'hideExport' ? (<CircularProgress color="secondary" size={18} style={{ marginLeft: '8px' }} />) : t('reportExport')}
              </Button>
            </Grid>}
        {!showOnly &&
            <Grid item xs={4} sm={2}>
              <Button
                  className={classes.cyberButton}
                  onClick={() => handleClick(true, false)}
                  variant='outlined'
                  fullWidth
                  disabled={loading === 'hideEmailExport'}
              >
                <Typography variant="button" noWrap>
                  {loading === 'hideEmailExport' ? (<CircularProgress color="secondary" size={18} style={{ marginLeft: '8px' }} />) : t('reportEmail')}
                </Typography>
              </Button>
            </Grid>}
        <Portal disablePortal={false}>
          <Snackbar open={!!openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(null)}>
          <Alert
              onClose={() => setOpenSnackbar(null)}
              variant="filled"
              severity={['exceedAllowedDays', 'UnknownProblem'].includes(openSnackbar) ? 'error' : openSnackbar === 'selectOneDevice' ? 'warning' : 'success'}
          >
            {t(openSnackbar)}
          </Alert>
        </Snackbar>
        </Portal>
      </Grid>
  );
});

export default ReportFilter;